// client/src/components/layout/Sidebar.js
import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  BarChart3,Layers,Gauge, ChevronDown, Clock, Users, Building2, DollarSign, FileText, Settings, Send, Archive, UserCircle, LogOut, Landmark, TrendingUp, ArrowUpDown, Upload, Download, Activity
} from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import '../../styles/Sidebar.css';

const Sidebar = forwardRef((props, ref) => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isExpanded, setIsExpanded] = useState(!isMobile);
  const [pendingUsersCount, setPendingUsersCount] = useState(0);
  const [openMenus, setOpenMenus] = useState({});
  
  // Funzione per gestire il toggle dei sottomenù
  const toggleSubmenu = (menuKey) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setIsExpanded(!mobile);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Fetch pending users count for admin
  useEffect(() => {
    if (user && user.role === 'admin') {
      const fetchPendingUsersCount = async () => {
        try {
          const res = await axios.get('/api/admin/pending-users');
          setPendingUsersCount(res.data.pendingUsers.length);
        } catch (err) {
          console.error('Error fetching pending users:');
        }
      };
      
      fetchPendingUsersCount();
      
      // Refresh count every minute
      const interval = setInterval(fetchPendingUsersCount, 60000);
      return () => clearInterval(interval);
    }
  }, [user]);
  
  // Cleanup body class on unmount
  useEffect(() => {
    return () => {
      document.body.classList.remove('sidebar-open');
    };
  }, []);
  
  // Toggle sidebar expansion on mobile con gestione body scroll
  const toggleSidebar = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    
    // Prevent body scroll when sidebar is open on mobile
    if (isMobile) {
      if (newExpanded) {
        document.body.classList.add('sidebar-open');
      } else {
        document.body.classList.remove('sidebar-open');
      }
    }
  };
  
  // NUOVO: Esponi la funzione toggleSidebar tramite ref
  useImperativeHandle(ref, () => ({
    toggleSidebar
  }));
  
  // Close sidebar when clicking on links (mobile only)
  const handleLinkClick = () => {
    if (isMobile && isExpanded) {
      setIsExpanded(false);
      document.body.classList.remove('sidebar-open');
    }
  };

  // Handle logout con chiusura sidebar
  const handleLogout = () => {
    logout();
    navigate('/login');
    // Close sidebar on mobile after logout
    if (isMobile) {
      setIsExpanded(false);
      document.body.classList.remove('sidebar-open');
    }
  };
  
  // Define navigation links based on user role
  const getNavLinks = () => {
    if (user.role === 'admin') {
      return [
        { to: '/admin/dashboard', textKey: 'sidebar.dashboard', icon: BarChart3 },
        { to: '/admin/pending-users', textKey: 'sidebar.pendingRegistrations', icon: Clock, badge: pendingUsersCount },
        { to: '/admin/users', textKey: 'sidebar.users', icon: Users },
        { to: '/admin/organizations', textKey: 'sidebar.organizations', icon: Building2 },
        { to: '/admin/requests', textKey: 'sidebar.liquidityRequests', icon: DollarSign },
        { to: '/admin/bids', textKey: 'sidebar.bids', icon: FileText },
        { to: '/admin/markets', textKey: 'sidebar.marketsManagement', icon: TrendingUp },
        { to: '/admin/profile', textKey: 'sidebar.profileSettings', icon: Settings }
      ];
    } else if (user.role === 'bank') {
    const bankLinks = [
      { to: '/bank/dashboard', textKey: 'sidebar.dashboard', icon: BarChart3 },
      { to: '/bank/open-requests', textKey: 'sidebar.newBids', icon: Send },
      { to: '/bank/my-bids', textKey: 'sidebar.pendingBids', icon: Clock },
      { to: '/bank/past-bids', textKey: 'sidebar.pastBids', icon: Archive },
      { to: '/bank/profile', textKey: 'sidebar.profileSettings', icon: UserCircle }
    ];
    
    // Mostra team management a tutti tranne ai referral
      if (user.isReferral !== true) {
      bankLinks.splice(-1, 0, { to: '/bank/team', textKey: 'sidebar.teamManagement', icon: Users });
    }
    
    return bankLinks;

} else { // company
    const isPaying = user?.organization?.isPaying !== false;
    const companyLinks = [
      { to: '/company/dashboard', textKey: 'sidebar.dashboard', icon: BarChart3 }
    ];

    // Nuova Richiesta: submenu per paganti, link singolo per non paganti
    if (isPaying) {
      companyLinks.push({
        textKey: 'sidebar.newRequest',
        icon: Send,
        isSubmenu: true,
        key: 'newRequest',
        submenuItems: [
          { to: '/company/new-request/allocation', textKey: 'sidebar.allocation', icon: Upload },
          { to: '/company/new-request/hot-money', textKey: 'sidebar.hotMoney', icon: Download }
        ]
      });
    } else {
      companyLinks.push({ to: '/company/new-request/allocation', textKey: 'sidebar.newRequest', icon: Send });
    }

    // Richieste Passate: submenu per paganti, link singolo per non paganti
    if (isPaying) {
      companyLinks.push({
        textKey: 'sidebar.pastRequests',
        icon: Archive,
        isSubmenu: true,
        key: 'pastRequests',
        submenuItems: [
          { to: '/company/past-requests/allocation', textKey: 'sidebar.allocations', icon: Upload },
          { to: '/company/past-requests/hot-money', textKey: 'sidebar.hotMoney', icon: Download }
        ]
      });
    } else {
      companyLinks.push({ to: '/company/past-requests/allocation', textKey: 'sidebar.pastRequests', icon: Archive });
    }
    
    // Se non pagante: aggiungi Team e Analytics come voci normali
if (user?.organization?.isPaying === false) {
  companyLinks.push({
    to: '/company/team',
    textKey: 'sidebar.teamManagement',
    icon: Users
  });
  companyLinks.push({
    to: '/company/analytics',
    textKey: 'sidebar.analytics',
    icon: TrendingUp
  });
} else {
  // Se pagante: crea il sottomenù "Gestisci"
  const manageSubmenu = {
    textKey: 'sidebar.manage',
    icon: Layers,
    isSubmenu: true,
    key: 'manage',
    submenuItems: [
      { to: '/company/team', textKey: 'sidebar.teamManagement', icon: Users },
      { to: '/company/bank-management', textKey: 'sidebar.bankManagement', icon: Landmark }
    ]
  };
  // Sottomenù Insights (solo per paganti)
  const insightsSubmenu = {
    textKey: 'sidebar.insights',
    icon: Gauge,
    isSubmenu: true,
    key: 'insights',
    submenuItems: [
      { to: '/company/performance', textKey: 'sidebar.performance', icon: Activity },
      { to: '/company/analytics', textKey: 'sidebar.analytics', icon: TrendingUp },
      { to: '/company/markets', textKey: 'sidebar.markets', icon: ArrowUpDown }
    ]
  };
  companyLinks.push(insightsSubmenu);

  companyLinks.push(manageSubmenu);
}
    
    companyLinks.push({ to: '/company/profile', textKey: 'sidebar.profileSettings', icon: UserCircle });
    
    return companyLinks;
  }
};
  
  const navLinks = getNavLinks();
  
  return (
    <>
      {/* Mobile toggle button */}
      {isMobile && (
        <button 
          className="sidebar-toggle" 
          onClick={toggleSidebar}
          aria-label={isExpanded ? t('sidebar.closeMenu') : t('sidebar.openMenu')}
        >
          {isExpanded ? '✕' : '☰'}
        </button>
      )}
      
      <div className={`sidebar ${isExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="sidebar-content">
          <div className="logo">
            <img 
              src="/logoBlu.png" 
              alt="Euracle Logo" 
              className="main-logo"
            />
          </div>
          
<ul className="nav-links">
  {navLinks.map((link, index) => {
    const IconComponent = link.icon;
    const translatedText = t(link.textKey);
    
    // Se è un sottomenù
    if (link.isSubmenu) {
      const isOpen = openMenus[link.key];
      const hasActiveChild = link.submenuItems.some(item => pathname === item.to);
      
      return (
        <li key={index} className="nav-item nav-item-submenu">
          <div 
            className={`nav-link-submenu ${hasActiveChild ? 'has-active-child' : ''}`}
            onClick={() => !isExpanded ? null : toggleSubmenu(link.key)}
            title={!isExpanded ? translatedText : ''}
          >
            <span className="nav-icon">
              <IconComponent size={20} />
            </span>
            {isExpanded && (
              <>
                <span className="nav-text">{translatedText}</span>
                <span className={`submenu-arrow ${isOpen ? 'open' : ''}`}>
                  <ChevronDown size={14} />
                </span>
              </>
            )}
          </div>
          
          {isExpanded && isOpen && (
            <ul className="submenu">
              {link.submenuItems.map((subItem, subIndex) => {
                const SubIconComponent = subItem.icon;
                const subTranslatedText = t(subItem.textKey);
                
                return (
                  <li 
                    key={subIndex}
                    className={`submenu-item ${pathname === subItem.to ? 'active' : ''}`}
                  >
                    <Link to={subItem.to} onClick={handleLinkClick}>
                      <span className="nav-icon">
                        <SubIconComponent size={18} />
                      </span>
                      <span className="nav-text">{subTranslatedText}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </li>
      );
    }
    
    // Link normale
    return (
      <li 
        key={index} 
        className={`nav-item ${pathname === link.to ? 'active' : ''}`}
        title={!isExpanded ? translatedText : ''}
      >
        <Link to={link.to} onClick={handleLinkClick}>
          <span className="nav-icon">
            <IconComponent size={20} />
          </span>
          {isExpanded && <span className="nav-text">{translatedText}</span>}
          {link.badge > 0 && (
            <span className="badge-notification">{link.badge}</span>
          )}
        </Link>
      </li>
    );
  })}
</ul>

          {/* Mobile User Controls Section */}
          {isMobile && isExpanded && (
            <div className="sidebar-mobile-controls">
              {/* User Info */}
              <div className="mobile-user-info">
                <div className="mobile-user-name">
                  <UserCircle size={20} />
                  <span>{user?.organization?.name || user?.username || 'Admin'}</span>
                </div>
              </div>

              {/* Language Selector - Only for non-admin users */}
              {user?.role !== 'admin' && (
                <div className="mobile-language-section">
                  <div className="mobile-language-wrapper">
                    <LanguageSelector />
                  </div>
                </div>
              )}

              {/* Logout Section */}
              <div className="mobile-logout-section">
                <button onClick={handleLogout} className="mobile-logout-btn">
                  <LogOut size={20} />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Overlay for mobile */}
      {isMobile && isExpanded && (
        <div className="sidebar-overlay" onClick={toggleSidebar}></div>
      )}
    </>
  );
});

// Aggiungi displayName per debugging
Sidebar.displayName = 'Sidebar';

export default Sidebar;